import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import {
  sendQueryToDaikoAI,
  executeDaikoAISQL,
  getAIMessageCount,
} from "../../api/ai";
import ROUTES from "../../constants/routes";
import { Link } from "react-router-dom";
import { IoSend } from "react-icons/io5";

import "./AIChatPage.scss";

const SUGGESTION_PILLS = [
  "What was my sales last month?",
  "How can I enhance my business?",
  "What are my top selling products?",
  "Show me my daily revenue trend",
  "Which items have the highest profit margin?",
  "What's my customer retention rate?",
  "Which hours are busiest in my store?",
  "What's my inventory turnover rate?",
  "Show me my employee performance metrics",
  "What are my peak sales periods?",
  "Which payment methods are most popular?",
  "What's my average transaction value?",
];

const AIChatPage = () => {
  const [messages, setMessages] = useState([]);
  const [remainingMessage, setRemainingMessage] = useState(0);
  const [input, setInput] = useState("");
  const [loadingCount, setLoadingCount] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [upgrade, setUpgrade] = useState(true);
  const [error, setError] = useState(null);
  const [usedSuggestions, setUsedSuggestions] = useState(new Set());
  const [lastMessageWithSuggestions, setLastMessageWithSuggestions] =
    useState(null);
  const messagesContainerRef = useRef(null);

  // Get dynamic values from Redux store
  const user = useSelector((state) => state.userReducer.value);
  const organization = useSelector((state) => state.organizationReducer.value);
  const branch = useSelector((state) => state.branchReducer.value);

  // Derived values for API calls
  const authToken = user?.token;
  const orgId = organization?.id;
  const branchId = branch?.id;

  const loadMessageCount = (orgId) => {
    setLoadingCount(true);

    getAIMessageCount(orgId)
      .then((res) => {
        const count = res.data?.remainingMessageCount;
        if (typeof count === "number") {
          setRemainingMessage(count);
        } else {
          setRemainingMessage(0);
        }
        if (res.data?.upgrade) {
          setUpgrade(true);
        } else {
          setUpgrade(false);
        }
      })
      .catch((err) => {
        const count = err?.response?.data?.remainingMessageCount;
        if (typeof count === "number") {
          setRemainingMessage(count);
        } else {
          setRemainingMessage(0);
        }
        if (err?.response?.data?.upgrade) {
          setUpgrade(true);
        } else {
          setUpgrade(false);
        }
      })
      .finally(() => {
        setLoadingCount(false);
      });
  };

  useEffect(() => {
    if (orgId) {
      loadMessageCount(orgId);
    }
  }, [orgId, messages]);

  const handleSuggestionClick = (suggestion) => {
    setInput(suggestion);
    handleSendMessage(suggestion);
    setUsedSuggestions((prev) => new Set([...prev, suggestion]));
    setInput("");
    setLastMessageWithSuggestions(null);
  };

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async (messageToSend = null) => {
    const message = messageToSend || input;
    if (!message.trim()) return;

    if (!authToken || !orgId) {
      setError(
        "User authentication or organization ID is missing. Cannot send message."
      );
      const errMessage = {
        id: `error-config-${Date.now()}`,
        sender: "ai",
        text: "Configuration error: Missing user token or organization ID. Please ensure you are logged in and an organization is active.",
        timestamp: new Date(),
        isError: true,
      };
      setMessages((prevMessages) => [...prevMessages, errMessage]);
      return;
    }

    const userMessage = {
      id: `user-${Date.now()}`,
      sender: "user",
      text: message,
      timestamp: new Date(),
    };

    setMessages((prevMessages) => [...prevMessages, userMessage]);
    setLastMessageWithSuggestions(null);
    if (!messageToSend) {
      setInput("");
    }
    setIsLoading(true);
    setError(null);

    try {
      const aiQueryResponse = await sendQueryToDaikoAI(
        message,
        authToken,
        orgId,
        branchId
      );
      let aiMessageContent = "Sorry, I could not process that response.";

      if (aiQueryResponse && aiQueryResponse.result) {
        if (aiQueryResponse.type === "text") {
          aiMessageContent = aiQueryResponse.result;
          const aiTextMessage = {
            id: `ai-${Date.now()}`,
            sender: "ai",
            text: aiMessageContent,
            timestamp: new Date(),
          };
          setMessages((prevMessages) => [...prevMessages, aiTextMessage]);
          setLastMessageWithSuggestions(aiTextMessage.id);
        } else if (aiQueryResponse.type === "sql") {
          const sqlQuery = aiQueryResponse.result;

          setIsLoading(true);
          let executionErrorOccurred = null;
          try {
            const sqlExecuteResponse = await executeDaikoAISQL(
              sqlQuery,
              aiQueryResponse.chat_id,
              authToken,
              orgId,
              branchId
            );

            if (
              sqlExecuteResponse &&
              typeof sqlExecuteResponse.summary_sentence === "string" &&
              sqlExecuteResponse.summary_sentence.trim() !== ""
            ) {
              aiMessageContent = sqlExecuteResponse.summary_sentence;
            } else if (
              sqlExecuteResponse &&
              Array.isArray(sqlExecuteResponse.data)
            ) {
              if (sqlExecuteResponse.data.length > 0) {
                aiMessageContent = `Query Results (summary was not provided):\n\`\`\`json\n${JSON.stringify(
                  sqlExecuteResponse.data,
                  null,
                  2
                )}\n\`\`\`\n`;
              } else {
                aiMessageContent =
                  "The query executed successfully but returned no results, and no summary was provided.";
              }
            } else if (sqlExecuteResponse && sqlExecuteResponse.message) {
              aiMessageContent = `SQL Execution Note: ${sqlExecuteResponse.message}`;
            } else {
              aiMessageContent =
                "The SQL query was executed, but the response format was unclear (no summary or data array).";
            }
          } catch (execErrorCaught) {
            console.error("Failed to execute SQL query:", execErrorCaught);
            aiMessageContent = `Error executing SQL: ${execErrorCaught.message}`;
            executionErrorOccurred = execErrorCaught;
          }
          const sqlResultMessage = {
            id: `ai-sql-result-${Date.now()}`,
            sender: "ai",
            text: aiMessageContent,
            timestamp: new Date(),
            isError: executionErrorOccurred ? true : false,
          };
          setMessages((prevMessages) => [...prevMessages, sqlResultMessage]);
          setLastMessageWithSuggestions(sqlResultMessage.id);
        } else {
          aiMessageContent = aiQueryResponse.result;
          const aiOtherMessage = {
            id: `ai-${Date.now()}`,
            sender: "ai",
            text: aiMessageContent,
            timestamp: new Date(),
          };
          setMessages((prevMessages) => [...prevMessages, aiOtherMessage]);
          setLastMessageWithSuggestions(aiOtherMessage.id);
        }
      } else if (aiQueryResponse && typeof aiQueryResponse === "string") {
        aiMessageContent = aiQueryResponse;
        const aiStringMessage = {
          id: `ai-${Date.now()}`,
          sender: "ai",
          text: aiMessageContent,
          timestamp: new Date(),
        };
        setMessages((prevMessages) => [...prevMessages, aiStringMessage]);
        setLastMessageWithSuggestions(aiStringMessage.id);
      } else {
        const aiErrorResponseMessage = {
          id: `ai-error-${Date.now()}`,
          sender: "ai",
          text: "Sorry, I received an unclear response from the AI query service.",
          timestamp: new Date(),
          isError: true,
        };
        setMessages((prevMessages) => [
          ...prevMessages,
          aiErrorResponseMessage,
        ]);
        setLastMessageWithSuggestions(aiErrorResponseMessage.id);
      }
    } catch (err) {
      console.error(
        "Failed to send message or process AI query response:",
        err
      );
      setError(err.message);
      const errorMessage = {
        id: `ai-error-${Date.now()}`,
        sender: "ai",
        text:
          err.message ||
          "An error occurred while contacting the AI query service.",
        timestamp: new Date(),
        isError: true,
      };
      setMessages((prevMessages) => [...prevMessages, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const [selectedSuggestions, setSelectedSuggestions] = useState([]);

  useEffect(() => {
    const unusedSuggestions = SUGGESTION_PILLS.filter(
      (suggestion) => !usedSuggestions.has(suggestion)
    );
    if (unusedSuggestions.length > 0) {
      const shuffled = [...unusedSuggestions].sort(() => 0.5 - Math.random());
      setSelectedSuggestions(shuffled.slice(0, 2));
    } else {
      setSelectedSuggestions([]);
    }
  }, [usedSuggestions]);

  const renderSuggestionPills = () => {
    if (selectedSuggestions.length === 0) return null;

    return (
      <div className="suggestion-pills">
        {selectedSuggestions.map((suggestion, index) => (
          <button
            key={index}
            className="suggestion-pill"
            onClick={() => handleSuggestionClick(suggestion)}
            disabled={isLoading}
          >
            {suggestion}
          </button>
        ))}
      </div>
    );
  };

  if (!authToken || !orgId) {
    return (
      <div className="ai-chat-page-container">
        <header className="desktop-navbar box-shadow ai-chat-header">
          <h1>AI Chat Assistant</h1>
        </header>
        <div className="ai-chat-content-wrapper">
          <div
            className="ai-chat-messages-area"
            style={{ justifyContent: "center", alignItems: "center" }}
          >
            <p style={{ textAlign: "center", padding: "20px" }}>
              AI Chat requires user authentication and an active organization.
              <br />
              Please log in and ensure an organization is selected.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ai-chat-page-container">
      <header className="desktop-navbar box-shadow ai-chat-header">
        <h1>AI Chat Assistant</h1>
      </header>
      <p className="ai-page-note">
        Note: We do not send User Data to Third Party Chatbots.
      </p>
      <div className="ai-chat-content-wrapper">
        <div className="ai-chat-messages-area" ref={messagesContainerRef}>
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`chat-message ${msg.sender} ${
                msg.isError ? "error-message" : ""
              }`}
            >
              <p className="message-text" style={{ whiteSpace: "pre-wrap" }}>
                {msg.text}
              </p>
              <span className="message-timestamp">
                {msg.timestamp.toLocaleTimeString()}
              </span>
            </div>
          ))}
          {isLoading && (
            <div className="chat-message ai loading-message" key="loading">
              <p className="message-text">AI is thinking...</p>
            </div>
          )}
          {messages.length === 0 && renderSuggestionPills()}
          {messages.length > 0 &&
            lastMessageWithSuggestions &&
            renderSuggestionPills()}
        </div>
        {remainingMessage >= 0 && (
          <div className="ai-chat-remaining-messgae">
            <p className={remainingMessage === 0 ? "zero left" : "left"}>
              {remainingMessage} message{remainingMessage > 1 ? "s" : ""}{" "}
              remaining
            </p>
            {upgrade ? (
              <Link to={ROUTES.upgrade.path}>
                <p>Upgrade</p>
              </Link>
            ) : (
              <></>
            )}
          </div>
        )}
        <div className="ai-chat-input-area">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) =>
              e.key === "Enter" && !isLoading && handleSendMessage()
            }
            placeholder="Type your message to the AI..."
            disabled={isLoading || !orgId}
          />
          <button
            onClick={() => handleSendMessage()}
            disabled={
              isLoading || loadingCount || !input.trim() || !authToken || !orgId
            }
          >
            <IoSend size={20} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AIChatPage;
